import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { getPlanByTier, PLANS, type PlanDefinition, type PlanTier } from '@/config/plans';

interface SubscriptionState {
  isLoading: boolean;
  isSubscribed: boolean;
  currentTier: PlanDefinition;
  productId: string | null;
  priceId: string | null;
  subscriptionEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

interface SubscriptionContextType extends SubscriptionState {
  refreshSubscription: () => Promise<void>;
  checkUsageLimit: (type: 'transcripts' | 'analyses' | 'audioMinutes', current: number, prepaidCredits?: number) => { 
    allowed: boolean; 
    limit: number; 
    percentage: number;
    source: 'monthly' | 'prepaid' | 'blocked';
    monthlyRemaining?: number;
    prepaidRemaining?: number;
  };
}

const defaultTier = PLANS.find(t => t.tier === 'free')!;

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth();
  const [state, setState] = useState<SubscriptionState>({
    isLoading: true,
    isSubscribed: false,
    currentTier: defaultTier,
    productId: null,
    priceId: null,
    subscriptionEnd: null,
    cancelAtPeriodEnd: false,
  });

  const refreshSubscription = useCallback(async () => {
    // Get fresh session directly from Supabase to avoid stale token issues
    const { data: sessionData } = await supabase.auth.getSession();
    const currentSession = sessionData?.session;
    
    if (!currentSession?.access_token) {
      setState({
        isLoading: false,
        isSubscribed: false,
        currentTier: defaultTier,
        productId: null,
        priceId: null,
        subscriptionEnd: null,
        cancelAtPeriodEnd: false,
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`,
        },
      });

      if (error) {
        // If it's an auth error, just return default state - session might be refreshing
        const errorMessage = error?.message || '';
        if (errorMessage.includes('Authentication') || errorMessage.includes('JWT') || errorMessage.includes('token')) {
          console.log('Session expired, waiting for refresh...');
          setState(prev => ({ ...prev, isLoading: false }));
          return;
        }
        console.error('Error checking subscription:', error);
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      // Resolve tier from the response - use tier field directly from new API
      const tier = data.tier ? getPlanByTier(data.tier) : defaultTier;

      setState({
        isLoading: false,
        isSubscribed: data.subscribed,
        currentTier: tier || defaultTier,
        productId: data.productId,
        priceId: data.priceId || null,
        subscriptionEnd: data.subscriptionEnd,
        cancelAtPeriodEnd: data.cancelAtPeriodEnd,
      });
    } catch (error) {
      console.error('Error refreshing subscription:', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  // Only check subscription when user is authenticated
  useEffect(() => {
    if (user) {
      refreshSubscription();
    } else {
      setState({
        isLoading: false,
        isSubscribed: false,
        currentTier: defaultTier,
        productId: null,
        priceId: null,
        subscriptionEnd: null,
        cancelAtPeriodEnd: false,
      });
    }
  }, [user, refreshSubscription]);

  // Refresh subscription every 5 minutes (not 60s — edge function cold starts are slow)
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(refreshSubscription, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, refreshSubscription]);

  const checkUsageLimit = useCallback((type: 'transcripts' | 'analyses' | 'audioMinutes', current: number, prepaidCredits: number = 0) => {
    let limit: number;
    
    switch (type) {
      case 'transcripts':
        limit = state.currentTier.limits.transcripts;
        break;
      case 'analyses':
        limit = state.currentTier.limits.analysesPerMonth;
        break;
      case 'audioMinutes':
        limit = state.currentTier.limits.audioMinutesPerMonth;
        break;
      default:
        limit = 0;
    }
    
    if (limit === -1) {
      return { allowed: true, limit: -1, percentage: 0, source: 'monthly' as const };
    }
    
    const monthlyRemaining = Math.max(0, limit - current);
    const totalRemaining = monthlyRemaining + prepaidCredits;
    const percentage = Math.round((current / limit) * 100);
    
    // If monthly quota is available
    if (current < limit) {
      return { 
        allowed: true, 
        limit, 
        percentage: Math.min(percentage, 100),
        source: 'monthly' as const,
        monthlyRemaining,
        prepaidRemaining: prepaidCredits,
      };
    }
    
    // Monthly exhausted, check prepaid credits
    if (prepaidCredits > 0) {
      return {
        allowed: true,
        limit,
        percentage: 100,
        source: 'prepaid' as const,
        monthlyRemaining: 0,
        prepaidRemaining: prepaidCredits,
      };
    }
    
    // Both exhausted
    return { 
      allowed: false, 
      limit, 
      percentage: 100,
      source: 'blocked' as const,
      monthlyRemaining: 0,
      prepaidRemaining: 0,
    };
  }, [state.currentTier]);

  return (
    <SubscriptionContext.Provider value={{ ...state, refreshSubscription, checkUsageLimit }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
